import React, {
  Fragment,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { useHistory } from 'react-router-dom'
import useKeyPress from '@scentregroup/shared/hooks/use-key-press'
import { useEvolvedContext } from '../../context'
import SearchResults from './search-results'
import SearchContainer from './search-container'
import SearchModalControls from './search-modal-controls'
import { SearchProps, isEmbeddedSearchProps } from './types'
import useAlgolia from './use-algolia'
import groupAndRenderSegments from './group-and-render-segments'
import {
  makeOnResultClick,
  logOnClearSearch,
  logHideSearch,
  logShowSearch,
  logOnResultsChange,
} from './analytics'
export type { AlgoliaApiKeys } from './types'

const ESCAPE_CODE = 'Escape'

function useShowHide(): [boolean, () => void, () => void] {
  const [isVisible, setIsVisible] = useState(false)
  const hide = useCallback((): void => {
    window.scroll(0, 0)
    setIsVisible(false)
  }, [])
  const show = useCallback((): void => {
    setIsVisible(true)
    logShowSearch()
  }, [])
  return [isVisible, show, hide]
}

type LocationListener = Parameters<ReturnType<typeof useHistory>['listen']>[0]

function useHistoryListener(listener: LocationListener): void {
  const history = useHistory()
  useEffect(() => (history.listen ? history.listen(listener) : () => null), [
    listener,
    history,
  ])
}

const Search: React.FC<SearchProps> = props => {
  const { algoliaApiKeys, country, centre } = props
  const isEvolved = useEvolvedContext()
  const [isUiLoading, setIsUiLoading] = useState(false)
  const initialQuery = isEmbeddedSearchProps(props)
    ? props.showFixedQuery
    : undefined
  const {
    query,
    isLoading: isDataLoading,
    results: segmentHits,
    setQuery,
    isInitialised,
  } = useAlgolia(algoliaApiKeys, isEvolved, country, centre, initialQuery)
  const context = { isEvolved, country, centre }
  const isLoading = isDataLoading || isUiLoading
  const onResultClickHeading = makeOnResultClick(query)
  const results = groupAndRenderSegments({
    segmentHits,
    context,
    onResultClickHeading,
  })
  const [isVisible, show, hide] = useShowHide()
  const hideManual = (): void => {
    logHideSearch(query)
    hide()
  }
  useKeyPress({ [ESCAPE_CODE]: hideManual })
  useHistoryListener(hide) // If the URL changes hide the search modal

  const onSubmit = async (): Promise<void> => {
    if (isEvolved && query.length > 0 && typeof window !== 'undefined') {
      setIsUiLoading(true)
      const centrePath = centre ? `/${centre.slug}` : ''
      const q = encodeURIComponent(query)
      // TODO when all clients are under one routing
      // use history.push from the router
      window.location.assign(`${centrePath}/search?q=${q}`)
      // // Required when using history.push:
      // hide() // in the event we're already on /search
      // setIsUiLoading(false)
    }
  }

  useEffect(() => {
    const numHits = results.reduce(
      (acc: number, { hits }: { hits: ReactNode[] }) => acc + hits.length,
      0
    )
    logOnResultsChange(query, numHits)
  }, [query, results])

  const showNoResults = query.length > 0 && results.length === 0
  const displayMode = isVisible ? 'visibleModal' : 'hiddenModal'
  const baseResultProps = { showNoResults, results, isLoading }
  if (!isInitialised) {
    return null
  } else if (isEmbeddedSearchProps(props)) {
    return (
      <SearchContainer displayMode="embedded">
        <SearchResults
          {...baseResultProps}
          showFixedQuery={props.showFixedQuery}
        />
      </SearchContainer>
    )
  } else {
    return (
      <Fragment>
        <SearchContainer displayMode={displayMode}>
          <SearchModalControls
            queryText={query}
            onQueryChange={setQuery}
            onQueryKeyDown={() => null}
            onClickClose={hideManual}
            onClickClear={() => {
              logOnClearSearch(query)
              setQuery('')
            }}
            onSubmit={onSubmit}
          />
          <SearchResults {...baseResultProps} />
        </SearchContainer>
        {props.children(show)}
      </Fragment>
    )
  }
}

export { Search as EmbeddedSearch }

export default Search
